import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import {
  Box,
  HStack,
  IconButton,
  Input,
  VStack,
} from '@chakra-ui/react';
import { DragDropContext, Draggable, DropResult, Droppable } from '@hello-pangea/dnd';
import React from 'react';

import DevSandboxTooltip from 'components/DevSandbox/DevSandboxTooltip';
import Text from 'components/Text';

import EntryForm from './EntryForm';
import { CVEntry, CVSection, CVSectionType } from '../types';

/** Heroicons outline SVGs — matches sidebar + tab icon style */
const SECTION_ICON_SVGS: Record<CVSectionType, React.ReactNode> = {
  experience: (
    <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
    </svg>
  ),
  education: (
    <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15v-3.75m0 0l5.25-3" />
    </svg>
  ),
  skills: (
    <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17l-5.1-5.1a2.12 2.12 0 113-3l5.1 5.1m0 0l2.83-2.83a2.12 2.12 0 113 3l-2.83 2.83m-3-3l-2.83 2.83a2.12 2.12 0 103 3l2.83-2.83m0 0l5.1 5.1a2.12 2.12 0 103-3l-5.1-5.1" />
    </svg>
  ),
  projects: (
    <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" />
    </svg>
  ),
  certifications: (
    <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
    </svg>
  ),
  languages: (
    <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 21l5.25-11.25L21 21m-9-3h7.5M3 5.621a48.474 48.474 0 016-.371m0 0c1.12 0 2.233.038 3.334.114M9 5.25V3m3.334 2.364C11.176 10.658 7.69 15.08 3 17.502m9.334-12.138c.896.061 1.785.147 2.666.257m-4.589 8.495a18.023 18.023 0 01-3.827-5.802" />
    </svg>
  ),
  hobbies: (
    <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
    </svg>
  ),
  interests: (
    <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
    </svg>
  ),
};

interface SectionFormProps {
  section: CVSection;
  onUpdateTitle: (title: string) => void;
  onUpdateEntry: (entryId: string, field: keyof CVEntry, value: string | string[]) => void;
  onRemoveEntry: (entryId: string) => void;
  onRemoveSection: () => void;
  onReorderEntries: (reorderedIds: string[]) => void;
  onAddEntry: () => void;
  onAddEntryToTop?: () => void;
}

const SectionForm = ({
  section,
  onUpdateTitle,
  onUpdateEntry,
  onRemoveEntry,
  onRemoveSection,
  onReorderEntries,
  onAddEntry,
  onAddEntryToTop,
}: SectionFormProps) => {
  const handleEntryDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;

    const ids = section.entries.map((e) => e.id);
    const [reordered] = ids.splice(result.source.index, 1);
    ids.splice(result.destination.index, 0, reordered);
    onReorderEntries(ids);
  };

  return (
    <VStack spacing={4} alignItems="stretch">
      {/* Section header — icon + title + delete */}
      <HStack justifyContent="space-between" alignItems="center">
        <HStack spacing={2} flex={1} minWidth={0}>
          <Box color="purple.300" display="flex" alignItems="center" flexShrink={0}>
            {SECTION_ICON_SVGS[section.type]}
          </Box>
          <Input
            value={section.title}
            onChange={(e) => onUpdateTitle(e.target.value)}
            variant="unstyled"
            fontWeight="semibold"
            size="sm"
            placeholder="Section title"
            _focus={{ borderBottom: '1px solid', borderColor: 'purple.400' }}
          />
          <Text fontSize="micro" opacity="tertiary" style={{ flexShrink: 0 }}>
            ({section.entries.length})
          </Text>
        </HStack>
        <DevSandboxTooltip label="Remove section">
          <IconButton
            aria-label="Remove section"
            icon={<DeleteIcon boxSize={3} />}
            size="xs"
            variant="ghost"
            colorScheme="red"
            onClick={onRemoveSection}
          />
        </DevSandboxTooltip>
      </HStack>

      {/* Add to top — for robust sections (experience, education, etc.) */}
      {onAddEntryToTop && (
        <HStack
          as="button"
          spacing={1.5}
          px={3}
          height="30px"
          borderRadius="md"
          border="1px dashed"
          borderColor="whiteAlpha.200"
          justifyContent="center"
          cursor="pointer"
          _hover={{ borderColor: 'purple.400', backgroundColor: 'whiteAlpha.50' }}
          transition="all 0.15s"
          onClick={onAddEntryToTop}
        >
          <AddIcon boxSize={2} color="purple.300" />
          <Text fontSize="micro" opacity="secondary">
            Add new (latest)
          </Text>
        </HStack>
      )}

      {/* Draggable Entries */}
      <DragDropContext onDragEnd={handleEntryDragEnd}>
        <Droppable droppableId={`entries-${section.id}`}>
          {(provided) => (
            <VStack
              ref={provided.innerRef}
              {...provided.droppableProps}
              spacing={3}
              alignItems="stretch"
            >
              {section.entries.map((entry, index) => (
                <Draggable key={entry.id} draggableId={entry.id} index={index}>
                  {(draggableProvided, snapshot) => (
                    <Box
                      ref={draggableProvided.innerRef}
                      {...draggableProvided.draggableProps}
                      style={draggableProvided.draggableProps.style}
                      opacity={snapshot.isDragging ? 0.8 : 1}
                    >
                      <EntryForm
                        entry={entry}
                        sectionType={section.type}
                        onChange={(field, value) => onUpdateEntry(entry.id, field, value)}
                        onRemove={() => onRemoveEntry(entry.id)}
                        canRemove={section.entries.length > 1}
                        dragHandleProps={section.entries.length > 1 ? draggableProvided.dragHandleProps : undefined}
                        onEnterAdd={index === section.entries.length - 1 ? onAddEntry : undefined}
                      />
                    </Box>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </VStack>
          )}
        </Droppable>
      </DragDropContext>
    </VStack>
  );
};

export default SectionForm;
